import { browserHistory, buildStudyRoutePath } from 'utils/navigation'
import { getPostLoginRedirectPath } from 'store/user'
import { createAuthRedirect } from 'utils/authRedirect'
import { hasValidSession } from 'utils/sessionUtils'
import { getUserScope, getUserScopeByStudy, getHighestUser, getUserRoleByStudy, checkConfigFeatures } from 'utils/misc'
import { getStudyVersionID } from 'utils/studyConfigUtils'

const redirectAction = newLoc => () => {
  browserHistory.replace(newLoc)
}

export const UserHasGlobalRoles = {}

export const UserHasFeatures = (
  Component,
  features,
  redirectSubroute,
  isNull = false,
  every = true,
  redirectIfEnabled = false,
  nullMeansEnabled = false,
) =>
  createAuthRedirect({
    authenticatedSelector: state => {
      const studyConfig = state.study.currentStudy.config
      if (isNull && studyConfig === null) return true
      const func = every ? 'every' : 'some'
      return features[func](feature => checkConfigFeatures(studyConfig, [feature], redirectIfEnabled, nullMeansEnabled))
    },
    redirectPath: state => {
      if (!Object.keys(state.user).length) return '/login'
      const currentStudy = state.study.currentStudy.id
      const studyVersionID = getStudyVersionID(state.location)
      return buildStudyRoutePath(currentStudy, studyVersionID, redirectSubroute)
    },
    redirectAction,
    wrapperDisplayName: 'UserStudyHasFeature',
  })(Component)

export const UserHasScopes = (scopeArray, Component, every = true, context = 'auto') =>
  createAuthRedirect({
    authenticatedSelector: state => {
      const func = every ? 'every' : 'some'
      if (!state?.user) return false
      let targetContext = context

      if (context === 'auto') {
        const currentPath = window.location.pathname
        const pathParts = currentPath.split('/').filter(Boolean)
        const isStudyRoute = pathParts[0] === 'studies' && pathParts.length >= 3
        const isCreateStudyRoute = currentPath.includes('/create/')

        if (isStudyRoute && !isCreateStudyRoute) {
          targetContext = 'study'
        } else {
          targetContext = 'global'
        }
      }

      if (targetContext === 'global') {
        const globalPermissions = getHighestUser({ user: state.user, context: 'global' }) || []
        return scopeArray[func](scope => globalPermissions.includes(scope))
      }

      if (targetContext === 'study') {
        let studyId = state.study?.currentStudy?.id
        if (!studyId) {
          const match = window.location.pathname.match(/\/studies\/(\d+)/)
          if (match) studyId = match[1]
        }
        if (!studyId) return false
        const currentStudy = state?.study?.currentStudy
        const studyPermissions =
          currentStudy?.id && String(currentStudy.id) === String(studyId)
            ? getUserScopeByStudy(state.user, currentStudy)
            : getUserScope(state.user, studyId)
        return scopeArray[func](scope => studyPermissions.includes(scope))
      }

      return false
    },
    redirectPath: state => (Object.keys(state.user).length ? '/unauthorized' : '/login'),
    redirectAction: newLoc => () => {
      browserHistory.replace(newLoc)
    },
    wrapperDisplayName: 'UserHasScopes',
  })(Component)

export const UserHasScope = (scopeArray, Component, every = true, studyID, redirectRoute, study) =>
  createAuthRedirect({
    authenticatedSelector: state => {
      const func = every ? 'every' : 'some'
      if (!state.user) return false
      // Match legacy behavior: only use custom_roles when an explicit study object is passed.
      // Prefer studyID from the route so guards work before currentStudy is hydrated.
      const scopes = study
        ? getUserScopeByStudy(state.user, study)
        : getUserScope(state.user, studyID || state.study?.currentStudy?.id)
      if (!scopes.length) return false
      return scopeArray[func](scope => scopes.includes(scope))
    },
    redirectPath: state => {
      if (redirectRoute) return redirectRoute
      return Object.keys(state.user).length ? '/unauthorized' : '/login'
    },
    redirectAction,
    wrapperDisplayName: 'UserHasScope',
  })(Component)

export const UserDoesNotHaveRole = (disallowedRoleArr, Component) =>
  createAuthRedirect({
    authenticatedSelector: state => {
      if (state.study.currentStudy.id) {
        return state.user && !disallowedRoleArr.includes(getUserRoleByStudy(state.user, state.study.currentStudy.id))
      }
      return state.user && !disallowedRoleArr.includes(getHighestUser({ user: state.user, getRoleName: true }))
    },
    redirectPath: state => (Object.keys(state.user).length ? '/unauthorized' : '/login'),
    redirectAction: newLoc => () => {
      browserHistory.replace(newLoc)
    },
    wrapperDisplayName: 'UserDoesNotHaveRole',
  })(Component)

export const UserHasRole = (allowedRoles, studyId, Component, redirectSubroute = '') =>
  createAuthRedirect({
    authenticatedSelector: state => {
      const lowercaseAllowedRoles = allowedRoles?.map(role => role.toLowerCase())
      return state.user && lowercaseAllowedRoles.includes(getUserRoleByStudy(state.user, studyId)?.toLowerCase())
    },
    redirectPath: state => {
      if (redirectSubroute) return redirectSubroute
      return Object.keys(state.user).length ? '/unauthorized' : '/login'
    },
    redirectAction,
    wrapperDisplayName: 'UserHasRole',
  })(Component)

export const UserHasValidPassword = createAuthRedirect({
  authenticatedSelector: state => state.user && !state.user.expiration_message,
  redirectPath: '/users/reset',
  redirectAction: newLoc => () => {
    browserHistory.replace(newLoc)
  },
  wrapperDisplayName: 'UserHasValidPW',
})

export const UserIsAuthenticated = createAuthRedirect({
  authenticatedSelector: state => hasValidSession(state?.user),
  redirectPath: '/login',
  redirectAction: newLoc => () => {
    browserHistory.replace(newLoc)
  },
  wrapperDisplayName: 'UserIsAuthenticated',
})

export const UserIsNotAuthenticated = createAuthRedirect({
  authenticatedSelector: state => !hasValidSession(state?.user),
  redirectPath: (state, ownProps) => {
    const queryRedirect = ownProps.location?.query?.redirect
    if (queryRedirect) return queryRedirect
    const lastPath = getPostLoginRedirectPath(state.user)
    return lastPath || '/studies'
  },
  redirectAction: newLoc => () => {
    browserHistory.replace(newLoc)
  },
  wrapperDisplayName: 'UserIsNotAuthenticated',
})
